/**
 * PowerSaving template component.
 */
import Container from '@components/atoms/container/powerSavingContainer';
import Focus from '@shared/types/enums/focus';
import PowerSavingInfoContainer from '@components/atoms/container/powerSavingInfoContainer';
import PowerSavingSwitcherContainer from '@components/atoms/container/powerSavingSwitcherContainer';
import SettingsInfo from '@components/atoms/text/diskCapacityInfo';
import SettingsTitle from '@components/atoms/text/diskCapacityTitle';
import Storage from '@storage';
import Switcher from '@components/elements/buttons/powerSavingSwitcher';
import TranslationManager from '@shared/assets/locale/translationsManager';
import Viewport from '@components/atoms/container/contentContainer';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { popRoute } from '@shared/store/slices/router.slice';
import { setFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch } from '@shared/store/hooks';
import { useCallback, useEffect, useMemo } from 'react';

/**
 * PowerSaving template component.
 */
export default function PowerSavingTemplate() {
  const { callbacks, literals, props } = usePowerSavingTemplate();
  return (
    <Viewport id={'powerSavingContainer'}>
      <Container {...callbacks.container}>
        <PowerSavingInfoContainer>
          <SettingsTitle>{literals.title}</SettingsTitle>
          <SettingsInfo
            dangerouslySetInnerHTML={{
              __html: literals.description,
            }}
          />
        </PowerSavingInfoContainer>
        <PowerSavingSwitcherContainer>
          <Switcher {...props.switcher} />
        </PowerSavingSwitcherContainer>
      </Container>
    </Viewport>
  );
}

/**
 * Hook to use the power saving template.
 * Handles the power saving template logic.
 */
function usePowerSavingTemplate() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onMouseEnter = useCallback(() => {
    dispatch(setFocus(Focus.POWER_SAVING));
  }, []);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    dispatch(setFocus(Focus.POWER_SAVING));
  }, []);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  // Power saving keyboard
  useKeyboard({
    focus: Focus.POWER_SAVING,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'BACK':
          dispatch(popRoute());
          break;
        case 'LEFT':
          dispatch(setFocus(Focus.MENU));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      callbacks: {
        container: {
          onMouseEnter,
        },
      },
      literals: {
        title: TranslationManager.get('PAGES.SETTINGS.POWER_SAVING.PAGE_TITLE'),
        description: TranslationManager.get(
          'PAGES.SETTINGS.POWER_SAVING.DESCRIPTION'
        ),
      },
      props: {
        switcher: {
          active:
            Storage.get('deadmanActive') === '1' ||
            !Storage.get('deadmanActive'),
        },
      },
    };
  }, [onMouseEnter]);
}
